/**
 * EsimSection — Horizontal carousel of eSIM destinations displayed in the
 * Studio Home tab, right below the MyShop section.
 *
 * Tap a card → opens the full eSIM marketplace modal.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Globe, Wifi, ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import EsimMarketplaceModal from './EsimMarketplaceModal';

const EsimSection = () => {
  const { t } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMarket, setOpenMarket] = useState(false);
  const [presetCountry, setPresetCountry] = useState(null);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/esim/countries');
      setCountries((res.data?.countries || []).slice(0, 30));
    } catch {
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const handleOpen = (country = null) => {
    setPresetCountry(country);
    setOpenMarket(true);
  };

  return (
    <section className="mt-6" data-testid="esim-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Wifi className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-base font-bold text-white leading-tight" data-testid="esim-section-title">
              My eSIM voyage
            </h2>
            <p className="text-[11px] text-white/60 leading-tight">{t('esim.sectionSubtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleOpen(null)}
          className="text-xs font-semibold text-white/80 hover:text-white flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 transition"
          data-testid="esim-see-all-btn"
        >
          {t('esim.seeAll')} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Hero CTA card */}
      <button
        type="button"
        onClick={() => handleOpen(null)}
        className="w-full mb-3 relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-600/40 via-purple-600/30 to-pink-600/30 p-4 text-left hover:scale-[1.01] active:scale-[0.99] transition"
        data-testid="esim-hero-cta"
      >
        <div className="absolute inset-0 opacity-30">
          <Globe className="absolute -right-4 -bottom-4 w-32 h-32 text-white" strokeWidth={0.5} />
        </div>
        <div className="relative">
          <h3 className="text-white font-bold text-lg leading-tight">{t('esim.browseTitle')}</h3>
          <p className="text-white/80 text-xs mt-0.5">{t('esim.browseSubtitle')}</p>
        </div>
      </button>

      {/* Country carousel */}
      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-24 h-32 rounded-xl bg-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : countries.length === 0 ? (
        <div className="text-center text-white/50 text-xs py-6" data-testid="esim-countries-empty">
          {t('esim.countriesEmpty')}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-none" data-testid="esim-countries-list">
          {countries.map((c) => (
            <button
              key={c.country_code + (c.country_slug || '')}
              type="button"
              onClick={() => handleOpen(c.country_code)}
              className="flex-shrink-0 w-24 snap-start text-left bg-white/5 hover:bg-white/10 active:scale-95 transition rounded-xl border border-white/10 overflow-hidden"
              data-testid={`esim-country-${c.country_code}`}
            >
              <div className="w-full h-20 bg-gradient-to-br from-indigo-500/20 to-pink-500/20 flex items-center justify-center overflow-hidden">
                {c.country_image ? (
                  <img
                    src={c.country_image}
                    alt={c.country_title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Globe className="w-8 h-8 text-white/40" />
                )}
              </div>
              <div className="p-2">
                <div className="text-[11px] font-semibold text-white truncate leading-tight">{c.country_title}</div>
                <div className="text-[10px] text-white/60 mt-0.5">
                  {t('esim.from')} ${c.min_price_usd?.toFixed(2)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {openMarket && (
        <EsimMarketplaceModal
          onClose={() => setOpenMarket(false)}
          presetCountryCode={presetCountry}
        />
      )}
    </section>
  );
};

export default EsimSection;
